// ═══════════════════════════════════════════════════════════════
// Impact — Assessment Detail Page
// View, edit, and delete a single damage assessment
// Route: /impact/assessment/:id
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button,
  Textarea, SimpleGrid, Image, Modal, Loader, Center,
} from '../../components/gab/GabComponents';
import { format } from 'date-fns';
import {
  IconArrowLeft, IconEdit, IconTrash, IconCheck, IconX,
  IconMapPin, IconPhoto, IconUser, IconCalendar, IconBackhoe,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { AssessmentPhoto } from '../../store/impactStore';
import { toast } from './lib/toast';
import DamageCategorySelector from './components/DamageCategorySelector';
import DamageTypeSelector from './components/DamageTypeSelector';
import SeverityRating from './components/SeverityRating';
import LocationInput from './components/LocationInput';
import type { LocationValue } from './components/LocationInput';
import MapPicker from './components/MapPicker';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const CATEGORY_COLORS: Record<string, string> = {
  affected: 'green',
  minor: 'yellow',
  major: 'orange',
  destroyed: 'red',
  inaccessible: 'violet',
};

function getTypeLabel(assessmentType: string) {
  if (assessmentType === 'individual') return 'Home (IA)';
  if (assessmentType === 'public') return 'Infrastructure (PA)';
  return assessmentType || 'Unknown';
}

function getPhotoSrc(photo: AssessmentPhoto): string {
  if (!photo.photoUrl) return '';
  if (photo.photoUrl.startsWith('data:') || photo.photoUrl.startsWith('http')) {
    return photo.photoUrl;
  }
  return `data:image/jpeg;base64,${photo.photoUrl}`;
}

export default function AssessmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assessments, loadPhotos, updateAssessment, deleteAssessment } = useImpactStore();
  const { createRequest } = useDebrisOpsStore();
  const { activeIncident, user } = useAuthStore();

  const assessment = assessments.find(a => a.id === Number(id));

  // Photos
  const [photos, setPhotos] = useState<AssessmentPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    damageCategory: '',
    severity: null as number | null,
    damageTypes: [] as string[],
    description: '',
    location: {
      lat: undefined as number | undefined,
      lng: undefined as number | undefined,
      address: '',
      county: '',
      state: '',
      fipsCode: '',
    },
  });

  // Tag for debris removal
  const [tagging, setTagging] = useState(false);
  const handleTagForDebris = async () => {
    if (!assessment) return;
    setTagging(true);
    try {
      const incidentId = activeIncident?.id?.toString() ?? '';
      await createRequest({
        incidentId,
        assessmentId: String(assessment.id),
        address: assessment.address || '',
        latitude: assessment.latitude != null ? String(assessment.latitude) : '',
        longitude: assessment.longitude != null ? String(assessment.longitude) : '',
        damageCategory: assessment.damageCategory || '',
        damageTypes: JSON.stringify(assessment.damageTypes || []),
        description: assessment.description || '',
        status: 'pending',
        taggedBy: user?.email ?? '',
        taggedAt: new Date().toISOString(),
        notes: `From Impact Assessment #${assessment.id}`,
      });
      notifications.show({ message: 'Tagged for debris removal — request sent to Debris module', color: 'green' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to tag', color: 'red' });
    } finally { setTagging(false); }
  };

  // Permissions
  const { canEdit: canEditModule } = useModulePermission('impact');
  const canEdit = canEditModule;
  const canDelete = canEditModule;

  // Load photos on mount
  useEffect(() => {
    if (!id) return;
    setPhotosLoading(true);
    loadPhotos(Number(id))
      .then(setPhotos)
      .catch(() => toast.error('Failed to load photos'))
      .finally(() => setPhotosLoading(false));
  }, [id, loadPhotos]);

  // Populate edit form when entering edit mode
  useEffect(() => {
    if (editing && assessment) {
      setEditForm({
        damageCategory: assessment.damageCategory || '',
        severity: assessment.severity,
        damageTypes: assessment.damageTypes || [],
        description: assessment.description || '',
        location: {
          lat: assessment.latitude ?? undefined,
          lng: assessment.longitude ?? undefined,
          address: assessment.address || '',
          county: assessment.county || '',
          state: assessment.stateCode || '',
          fipsCode: assessment.fipsCode || '',
        },
      });
    }
  }, [editing, assessment]);

  if (!assessment) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Assessment not found</Text>
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/impact/assessments')}>
            Back to Assessments
          </Button>
        </Stack>
      </Center>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAssessment(Number(id), {
        damageCategory: editForm.damageCategory,
        severity: editForm.severity,
        damageTypes: editForm.damageTypes,
        description: editForm.description,
        address: editForm.location.address,
        county: editForm.location.county,
        stateCode: editForm.location.state,
        fipsCode: editForm.location.fipsCode,
        latitude: editForm.location.lat ?? null,
        longitude: editForm.location.lng ?? null,
      });
      toast.success('Assessment updated');
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this assessment? This action cannot be undone.')) return;
    try {
      await deleteAssessment(assessment);
      toast.success('Assessment deleted');
      navigate('/impact/assessments');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleLocationChange = (loc: LocationValue) => {
    setEditForm(prev => ({
      ...prev,
      location: {
        lat: loc.lat,
        lng: loc.lng,
        address: loc.address || '',
        county: loc.county || '',
        state: loc.state || '',
        fipsCode: loc.fipsCode || '',
      },
    }));
  };

  const handleMapChange = (pos: { lat: number; lng: number }) => {
    setEditForm(prev => ({
      ...prev,
      location: { ...prev.location, lat: pos.lat, lng: pos.lng },
    }));
  };

  const formattedDate = assessment.datecreated
    ? (() => { try { return format(new Date(assessment.datecreated), 'MMM d, yyyy h:mm a'); } catch { return assessment.datecreated; } })()
    : 'Unknown';

  return (
    <div>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'Impact', to: '/impact' },
        { label: 'Assessments', to: '/impact/assessments' },
        { label: assessment?.address || 'Assessment' },
      ]} />
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/impact/assessments')}
            size="sm"
          >
            Back
          </Button>
          <Title order={3} c="black">Assessment #{assessment.id}</Title>
          <Badge color={CATEGORY_COLORS[assessment.damageCategory] || 'gray'} size="lg">
            {assessment.damageCategory || 'Uncategorized'}
          </Badge>
          <Badge variant="outline" size="lg">
            {getTypeLabel(assessment.assessmentType)}
          </Badge>
        </Group>

        <Group gap="xs">
          {!editing && canEdit && (
            <Button
              leftSection={<IconBackhoe size={14} />}
              variant="light"
              color="orange"
              onClick={handleTagForDebris}
              loading={tagging}
              size="sm"
            >
              Tag for Debris Removal
            </Button>
          )}
          {!editing && canEdit && (
            <Button
              leftSection={<IconEdit size={14} />}
              variant="light"
              onClick={() => setEditing(true)}
              size="sm"
            >
              Edit
            </Button>
          )}
          {editing && (
            <>
              <Button
                leftSection={<IconCheck size={14} />}
                onClick={handleSave}
                loading={saving}
                size="sm"
              >
                Save
              </Button>
              <Button
                leftSection={<IconX size={14} />}
                variant="subtle"
                onClick={() => setEditing(false)}
                size="sm"
              >
                Cancel
              </Button>
            </>
          )}
          {!editing && canDelete && (
            <Button
              leftSection={<IconTrash size={14} />}
              color="red"
              variant="subtle"
              onClick={handleDelete}
              size="sm"
            >
              Delete
            </Button>
          )}
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* Left Column — Details */}
        <Stack gap="md">
          {/* Damage Assessment */}
          <Paper p="lg" withBorder radius="sm">
            <Text fw={600} size="sm" mb="sm">Damage Assessment</Text>

            {editing ? (
              <Stack gap="sm">
                <DamageCategorySelector
                  value={editForm.damageCategory}
                  onChange={(val) => setEditForm(prev => ({ ...prev, damageCategory: val }))}
                />
                {assessment.assessmentType !== 'individual' && (
                  <SeverityRating
                    value={editForm.severity}
                    onChange={(val) => setEditForm(prev => ({ ...prev, severity: val }))}
                  />
                )}
                <DamageTypeSelector
                  value={editForm.damageTypes}
                  onChange={(val) => setEditForm(prev => ({ ...prev, damageTypes: val }))}
                  mode={assessment.assessmentType === 'individual' ? 'ia' : assessment.assessmentType === 'public' ? 'pa' : 'all'}
                />
                <Textarea
                  label="Description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  minRows={3}
                  autosize
                />
              </Stack>
            ) : (
              <Stack gap="xs">
                <Group gap="lg">
                  <div>
                    <Text size="xs" c="dimmed">Category</Text>
                    <Badge color={CATEGORY_COLORS[assessment.damageCategory] || 'gray'}>
                      {assessment.damageCategory || 'N/A'}
                    </Badge>
                  </div>
                  {assessment.assessmentType !== 'individual' && (
                    <div>
                      <Text size="xs" c="dimmed">Severity</Text>
                      <Badge variant="outline">
                        {assessment.severity != null ? `${assessment.severity} / 5` : 'N/A'}
                      </Badge>
                    </div>
                  )}
                  <div>
                    <Text size="xs" c="dimmed">Type</Text>
                    <Text size="sm">{getTypeLabel(assessment.assessmentType)}</Text>
                  </div>
                </Group>

                {assessment.damageTypes?.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Damage Types</Text>
                    <Group gap={4}>
                      {assessment.damageTypes.map(dt => (
                        <Badge key={dt} size="sm" variant="outline">{dt}</Badge>
                      ))}
                    </Group>
                  </div>
                )}

                {assessment.description && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Description</Text>
                    <Text size="sm">{assessment.description}</Text>
                  </div>
                )}
              </Stack>
            )}
          </Paper>

          {/* Residence Details (IA only) */}
          {assessment.assessmentType === 'individual' && (
            <Paper p="lg" withBorder radius="sm">
              <Text fw={600} size="sm" mb="sm">Residence Details</Text>
              <SimpleGrid cols={2} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Residence Type</Text>
                  <Text size="sm">{assessment.residenceType || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Ownership</Text>
                  <Text size="sm">{assessment.ownership || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Dwelling Type</Text>
                  <Text size="sm">{assessment.dwellingType || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Habitable</Text>
                  <Text size="sm">
                    {assessment.habitable != null ? (assessment.habitable ? 'Yes' : 'No') : 'N/A'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Insured</Text>
                  <Text size="sm">
                    {assessment.insured != null ? (assessment.insured ? 'Yes' : 'No') : 'N/A'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Occupants</Text>
                  <Text size="sm">{assessment.occupants || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Water Line Height</Text>
                  <Text size="sm">{assessment.waterLineHeight ? `${assessment.waterLineHeight}"` : 'N/A'}</Text>
                </div>
              </SimpleGrid>
            </Paper>
          )}

          {/* Facility Details (PA only) */}
          {(assessment.assessmentType === 'public' || assessment.assessmentType === 'infrastructure') && (
            <Paper p="lg" withBorder radius="sm">
              <Text fw={600} size="sm" mb="sm">Facility Details</Text>
              <SimpleGrid cols={2} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Facility Name</Text>
                  <Text size="sm">{assessment.facilityName || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Facility Type</Text>
                  <Text size="sm">{assessment.facilityType || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">PA Category</Text>
                  <Text size="sm">{assessment.paCategory || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Estimated Repair Cost</Text>
                  <Text size="sm">{assessment.repairCost ? '$' + parseFloat(assessment.repairCost).toLocaleString() : 'N/A'}</Text>
                </div>
              </SimpleGrid>
            </Paper>
          )}

          {/* Bulk Entry Details */}
          {assessment.bulkEntry && (
            <Paper p="lg" withBorder radius="sm">
              <Text fw={600} size="sm" mb="sm">Bulk Entry</Text>
              <SimpleGrid cols={2} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Neighborhood</Text>
                  <Text size="sm">{assessment.neighborhood || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Home Count</Text>
                  <Text size="sm">{assessment.homeCount}</Text>
                </div>
              </SimpleGrid>
            </Paper>
          )}

          {/* Activity / Meta */}
          <Paper p="lg" withBorder radius="sm">
            <Text fw={600} size="sm" mb="sm">Activity</Text>
            <Stack gap="xs">
              <Group gap="xs">
                <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">Created:</Text>
                <Text size="sm">{formattedDate}</Text>
              </Group>
              <Group gap="xs">
                <IconUser size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">Assessor:</Text>
                <Text size="sm">{assessment.assessorName || 'Unknown'}</Text>
              </Group>
              {assessment.assessorEmail && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed" ml={22}>Email:</Text>
                  <Text size="sm">{assessment.assessorEmail}</Text>
                </Group>
              )}
              {assessment.method && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed" ml={22}>Method:</Text>
                  <Badge size="sm" variant="outline">{assessment.method}</Badge>
                </Group>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Right Column — Location + Photos */}
        <Stack gap="md">
          {/* Location */}
          <Paper p="lg" withBorder radius="sm">
            <Text fw={600} size="sm" mb="sm">
              <IconMapPin size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Location
            </Text>

            {editing ? (
              <Stack gap="sm">
                <LocationInput
                  value={{
                    lat: editForm.location.lat,
                    lng: editForm.location.lng,
                    address: editForm.location.address,
                    county: editForm.location.county,
                    state: editForm.location.state,
                    fipsCode: editForm.location.fipsCode,
                  }}
                  onChange={handleLocationChange}
                />
                <MapPicker
                  value={editForm.location.lat != null && editForm.location.lng != null
                    ? { lat: editForm.location.lat, lng: editForm.location.lng }
                    : null}
                  onChange={handleMapChange}
                  height={250}
                />
              </Stack>
            ) : (
              <Stack gap="xs">
                {assessment.address && (
                  <Text size="sm">{assessment.address}</Text>
                )}
                <SimpleGrid cols={2} spacing="xs">
                  {assessment.county && (
                    <div>
                      <Text size="xs" c="dimmed">County</Text>
                      <Text size="sm">{assessment.county}</Text>
                    </div>
                  )}
                  {assessment.stateCode && (
                    <div>
                      <Text size="xs" c="dimmed">State</Text>
                      <Text size="sm">{assessment.stateCode}</Text>
                    </div>
                  )}
                  {assessment.fipsCode && (
                    <div>
                      <Text size="xs" c="dimmed">FIPS Code</Text>
                      <Text size="sm">{assessment.fipsCode}</Text>
                    </div>
                  )}
                  {assessment.latitude != null && assessment.longitude != null && (
                    <div>
                      <Text size="xs" c="dimmed">Coordinates</Text>
                      <Text size="sm">{assessment.latitude.toFixed(6)}, {assessment.longitude.toFixed(6)}</Text>
                    </div>
                  )}
                </SimpleGrid>

                {assessment.latitude != null && assessment.longitude != null && (
                  <MapPicker
                    value={{ lat: assessment.latitude, lng: assessment.longitude }}
                    onChange={() => {}}
                    height={250}
                  />
                )}
              </Stack>
            )}
          </Paper>

          {/* Photos */}
          <Paper p="lg" withBorder radius="sm">
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">
                <IconPhoto size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Photos
              </Text>
              <Badge size="sm" variant="light">{photos.length}</Badge>
            </Group>

            {photosLoading ? (
              <Center py="lg">
                <Loader size="sm" />
              </Center>
            ) : photos.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="lg">No photos attached</Text>
            ) : (
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                {photos.map((photo, i) => (
                  <Paper
                    key={photo.id || i}
                    withBorder
                    radius="sm"
                    style={{ overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => setLightboxPhoto(getPhotoSrc(photo))}
                  >
                    <Image
                      src={getPhotoSrc(photo)}
                      h={120}
                      fit="cover"
                      fallbackSrc="https://placehold.co/200x120?text=No+Image"
                    />
                    {(photo.caption || photo.exifDate) && (
                      <Stack gap={2} p={4}>
                        {photo.caption && <Text size="xs" lineClamp={1}>{photo.caption}</Text>}
                        {photo.exifDate && <Text size="xs" c="dimmed">{photo.exifDate}</Text>}
                      </Stack>
                    )}
                  </Paper>
                ))}
              </SimpleGrid>
            )}
          </Paper>
        </Stack>
      </SimpleGrid>

      {/* Photo Lightbox */}
      <Modal
        opened={!!lightboxPhoto}
        onClose={() => setLightboxPhoto(null)}
        size="xl"
        withCloseButton
        centered
        padding={0}
      >
        {lightboxPhoto && (
          <Image src={lightboxPhoto} fit="contain" mah="80vh" />
        )}
      </Modal>
    </div>
  );
}
